import { useMemo, useState } from "react";
import { useMedia, usePageMeta, MEDIA_CATEGORIES } from "@/lib/content";
import { fileUrl } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";
import Lightbox from "@/components/site/Lightbox";
import EmbedConsent from "@/components/site/EmbedConsent";

export default function Media() {
  usePageMeta(
    "Media — SEIMEN DEXTER | Photos, Videos, DJ Sets",
    "Photos, videos and DJ sets of SEIMEN DEXTER — Hard / Industrial Techno from Mannheim, Germany."
  );
  const { data: media, isLoading } = useMedia();
  const [cat, setCat] = useState("all");
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const filtered = useMemo(
    () => (media || []).filter((m) => cat === "all" || m.category === cat),
    [media, cat]
  );
  const images = filtered.filter((m) => m.type === "image");
  const embeds = filtered.filter((m) => m.type !== "image");

  return (
    <div className="px-5 md:px-10 pt-28 md:pt-40 pb-24 md:pb-36">
      <Reveal>
        <p className="kicker mb-6">MEDIA</p>
        <h1 className="font-display text-6xl md:text-8xl leading-none mb-4 md:mb-6">FROM THE FLOOR.</h1>
        <p className="kicker">LIVE — RAVE — BACKSTAGE — PRESS</p>
      </Reveal>

      <div data-testid="media-filters" className="mt-12 md:mt-16 flex flex-wrap gap-2">
        {MEDIA_CATEGORIES.map((c) => (
          <button
            key={c.key}
            data-testid={`media-filter-${c.key}`}
            onClick={() => setCat(c.key)}
            aria-pressed={cat === c.key}
            className={`px-4 py-2.5 text-[10px] tracking-[0.25em] border transition-colors ${
              cat === c.key ? "bg-white text-black border-white" : "border-line text-white/60 hover:border-white/50 hover:text-white"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="mt-14 border-t border-line py-16 text-white/40 text-sm">LOADING…</div>
      ) : filtered.length === 0 ? (
        <div data-testid="media-empty" className="mt-14 border-t border-line py-16 md:py-20">
          <p className="font-display text-4xl md:text-6xl">MEDIA ARCHIVE COMING.</p>
          <p className="kicker mt-4">PHOTOS AND VIDEOS WILL BE PUBLISHED HERE</p>
        </div>
      ) : (
        <div data-testid="media-grid" className="mt-12 md:mt-16 columns-2 md:columns-3 gap-2 md:gap-3 [&>*]:mb-2 md:[&>*]:mb-3">
          {images.map((m, i) => (
            <button
              key={m.id}
              data-testid={`media-image-${m.id}`}
              onClick={() => setLightboxIndex(i)}
              className="block w-full text-left group break-inside-avoid"
              aria-label={m.title || "Open image"}
            >
              <img
                src={fileUrl(m.thumbnail_url || m.url)}
                alt={m.title || "SEIMEN DEXTER media"}
                loading="lazy"
                className="w-full object-cover border border-line transition-opacity group-hover:opacity-80"
                draggable="false"
              />
            </button>
          ))}
          {embeds.map((m) => (
            <div key={m.id} data-testid={`media-embed-${m.id}`} className="break-inside-avoid">
              <EmbedConsent
                type={m.type === "djset" ? "soundcloud" : "youtube"}
                url={m.url}
                thumbnail={m.thumbnail_url ? fileUrl(m.thumbnail_url) : undefined}
                title={m.title || "Embedded media"}
                aspect="aspect-video"
              />
              {m.title && <p className="text-[11px] tracking-[0.2em] text-white/50 mt-2">{m.title}</p>}
            </div>
          ))}
        </div>
      )}

      <Lightbox
        items={images.map((m) => ({ id: m.id, url: fileUrl(m.url), title: m.title }))}
        index={lightboxIndex}
        onNav={setLightboxIndex}
        onClose={() => setLightboxIndex(null)}
      />
    </div>
  );
}
